import { Experiment, ExperimentUser } from "@amplitude/experiment-react-native-client";
import Constants from "expo-constants";
import { getAppConfig } from "@src/config/legacyKitConfig";
import { appStorage, AppStorageKeys } from "@src/services/app-storage";
import { create } from "zustand";
import { isE2ETest } from "@src/modules/LaunchArguments";

export interface FeatureFlags {
  [key: string]: boolean | string | number | undefined;
}

interface VariantDetails {
  [key: string]: {
    value: string | undefined;
    payload?: any;
  };
}

interface FeatureFlagsState {
  flags: FeatureFlags;
  variantDetails: VariantDetails;
  localOverrides: FeatureFlags;
  isInitialized: boolean;
  isLoading: boolean;
  /** Whether feature flags have been successfully fetched from the server at least once */
  hasFetchedSuccessfully: boolean;
  /** Error message if the last fetch failed */
  fetchError: string | null;
  setFlags: (flags: FeatureFlags) => void;
  setVariantDetails: (details: VariantDetails) => void;
  setLocalOverrides: (overrides: FeatureFlags) => void;
  setIsInitialized: (value: boolean) => void;
  setIsLoading: (value: boolean) => void;
  setHasFetchedSuccessfully: (value: boolean) => void;
  setFetchError: (error: string | null) => void;
}

const useFeatureFlagsStore = create<FeatureFlagsState>()((set) => ({
  flags: {},
  variantDetails: {},
  localOverrides: {},
  isInitialized: false,
  isLoading: false,
  hasFetchedSuccessfully: false,
  fetchError: null,
  setFlags: (flags) => set({ flags }),
  setVariantDetails: (variantDetails) => set({ variantDetails }),
  setLocalOverrides: (localOverrides) => set({ localOverrides }),
  setIsInitialized: (value) => set({ isInitialized: value }),
  setIsLoading: (value) => set({ isLoading: value }),
  setHasFetchedSuccessfully: (value) => set({ hasFetchedSuccessfully: value }),
  setFetchError: (error) => set({ fetchError: error }),
}));

let experimentClient: ReturnType<typeof Experiment.initializeWithAmplitudeAnalytics> | null = null;

const hydrateFromCache = (): void => {
  const store = useFeatureFlagsStore.getState();
  const cachedFlags = appStorage.get<FeatureFlags>(AppStorageKeys.FEATURE_FLAGS);
  if (cachedFlags) {
    store.setFlags(cachedFlags);
  }

  const cachedOverrides = appStorage.get<FeatureFlags>(AppStorageKeys.FEATURE_FLAGS_OVERRIDES);
  if (cachedOverrides) {
    store.setLocalOverrides(cachedOverrides);
  }
};

/**
 * Load cached flags/overrides synchronously (no network).
 * Useful for early decisions during app bootstrap.
 */
export const hydrateFeatureFlagsFromCache = (): void => {
  hydrateFromCache();
};

/**
 * Check if there are cached feature flags available.
 */
export const hasCachedFeatureFlags = (): boolean => {
  const cachedFlags = appStorage.get<FeatureFlags>(AppStorageKeys.FEATURE_FLAGS);
  return cachedFlags !== null && cachedFlags !== undefined && Object.keys(cachedFlags).length > 0;
};

/**
 * Initialize the Amplitude Experiment client (after Analytics is initialized)
 * @returns true if feature flags are available (fresh fetch or cache), false otherwise
 */
export const initFeatureFlags = async (): Promise<boolean> => {
  const store = useFeatureFlagsStore.getState();

  if (store.isInitialized) {
    await fetchFlags();
    return useFeatureFlagsStore.getState().hasFetchedSuccessfully || hasCachedFeatureFlags();
  }

  // Skip feature flag fetching in E2E tests to avoid network dependencies
  if (isE2ETest()) {
    console.log("[FeatureFlags] E2E test mode - skipping Amplitude fetch");
    store.setIsInitialized(true);
    store.setHasFetchedSuccessfully(true);
    store.setLocalOverrides({ [FEATURE_FLAGS.LEGACY_AUTH]: "on" });
    return true;
  }

  store.setIsLoading(true);

  try {
    const config = getAppConfig();
    const deploymentKey =
      (Constants.expoConfig?.extra?.amplitudeDeploymentKey as string | undefined) ?? config.amplitudeApiKey;

    if (!deploymentKey) {
      console.warn("[FeatureFlags] No deployment key found");
      store.setIsLoading(false);
      return hasCachedFeatureFlags();
    }

    hydrateFromCache();

    // Initialize Experiment client
    experimentClient = Experiment.initializeWithAmplitudeAnalytics(deploymentKey, {
      serverZone: "EU",
      fetchOnStart: false,
      automaticFetchOnAmplitudeIdentityChange: true,
    });

    await fetchFlags();
    store.setIsInitialized(true);

    return useFeatureFlagsStore.getState().hasFetchedSuccessfully || hasCachedFeatureFlags();
  } catch (error) {
    console.error("[FeatureFlags] Init failed:", error);
    return hasCachedFeatureFlags();
  } finally {
    store.setIsLoading(false);
  }
};

/**
 * Build ExperimentUser with current user context for targeting.
 * Uses cached tenant context from last session for initial fetch.
 * TenantProvider also calls identifyCurrentUserWithAmplitude() once fresh
 * tenants are loaded, which updates Amplitude with current tenant info.
 */
const buildExperimentUser = (): ExperimentUser | undefined => {
  // Lazy require to avoid circular deps with auth-store
  const { useAuthStore } = require("@src/features/auth/auth-store");
  const { getCachedTenantContextSync } = require("@src/features/tenants/last-used-tenant-store");

  const authState = useAuthStore.getState();

  if (!authState.isAuthenticated || !authState.user) {
    return undefined;
  }

  // Get cached tenant context for targeting (may be null on first launch)
  const tenantContext = getCachedTenantContextSync();

  return {
    user_id: authState.user.id,
    user_properties: {
      ...(authState.user.email && { email: authState.user.email }),
      ...(authState.user.name && { name: authState.user.name }),
      // Include cached tenant context for feature flag targeting
      ...(tenantContext && {
        realm: tenantContext.realm,
        tenantId: tenantContext.tenantId,
        tenants: tenantContext.allTenantIds,
        realms: tenantContext.allRealms,
      }),
    },
  };
};

/**
 * Fetch feature flags from Amplitude Experiment
 */
export const fetchFlags = async (): Promise<void> => {
  // Skip fetching in E2E tests - use default values
  if (isE2ETest()) {
    const store = useFeatureFlagsStore.getState();
    if (!store.hasFetchedSuccessfully) {
      console.log("[FeatureFlags] E2E test mode - marking flags as ready with defaults");
      store.setHasFetchedSuccessfully(true);
    }
    return;
  }

  if (!experimentClient) {
    return;
  }

  const store = useFeatureFlagsStore.getState();

  try {
    store.setIsLoading(true);
    store.setFetchError(null);

    const experimentUser = buildExperimentUser();
    await experimentClient.fetch(experimentUser);

    const variants = experimentClient.all();
    const flags: FeatureFlags = {};
    const variantDetails: VariantDetails = {};

    Object.entries(variants).forEach(([key, variant]) => {
      flags[key] = variant.value;
      variantDetails[key] = {
        value: variant.value,
        payload: variant.payload,
      };
    });

    store.setFlags(flags);
    store.setVariantDetails(variantDetails);
    store.setHasFetchedSuccessfully(true);
    appStorage.set(AppStorageKeys.FEATURE_FLAGS, flags);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Failed to fetch feature flags";
    console.error("[FeatureFlags] Fetch failed:", error);
    store.setFetchError(errorMessage);
  } finally {
    useFeatureFlagsStore.getState().setIsLoading(false);
  }
};

/**
 * Check if value is a boolean variant ("enabled"/"disabled", "on"/"off", etc.)
 */
const isBooleanVariant = (value: any): boolean => {
  if (typeof value === "boolean") return true;
  if (typeof value === "string") {
    const n = value.toLowerCase();
    return ["enabled", "disabled", "on", "off", "true", "false"].includes(n);
  }
  return false;
};

/**
 * Convert variant to boolean
 */
const variantToBoolean = (value: any): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const n = value.toLowerCase();
    return n === "enabled" || n === "on" || n === "true";
  }
  return false;
};

/**
 * Get a feature flag value by name (priority: local override > Amplitude > zero-value)
 * @internal Use getBooleanFlag, getStringFlag, or getNumberFlag instead
 */
const getFlagByName = <T extends boolean | string | number>(flagName: string, zeroValue: T): T => {
  const store = useFeatureFlagsStore.getState();

  const overrideValue = store.localOverrides[flagName];
  if (overrideValue !== undefined) {
    return overrideValue as T;
  }

  const flagValue = store.flags[flagName];
  if (flagValue !== undefined) {
    return flagValue as T;
  }

  return zeroValue;
};

/**
 * Get a feature flag value (priority: local override > Amplitude > zero-value)
 * @deprecated Use the typed flag getters (getBooleanFlag, getStringFlag, getNumberFlag) with FEATURE_FLAGS instead
 */
export const getFlag = <T extends boolean | string | number>(flagName: string, zeroValue: T): T => {
  return getFlagByName(flagName, zeroValue);
};

/**
 * Get a boolean feature flag (returns false when not in store)
 */
export const getBooleanFlag = (flagName: string, defaultValue: boolean = false): boolean => {
  const value = getFlagByName<boolean | string>(flagName, defaultValue);
  return variantToBoolean(value);
};

/**
 * Get a string feature flag (returns "" when not in store)
 */
export const getStringFlag = (flagName: string): string => {
  return getFlagByName<string>(flagName, "");
};

/**
 * Get a number feature flag (returns 0 when not in store)
 */
export const getNumberFlag = (flagName: string): number => {
  return getFlagByName<number>(flagName, 0);
};

/**
 * Get all feature flags
 */
export const getAllFlags = (): FeatureFlags => {
  return useFeatureFlagsStore.getState().flags;
};

/**
 * Get the payload for a feature flag
 */
export const getFlagPayload = <T = any>(flagName: string, defaultValue?: T): T | undefined => {
  const store = useFeatureFlagsStore.getState();
  const variantDetails = store.variantDetails[flagName];
  const payload = variantDetails?.payload !== undefined ? (variantDetails.payload as T) : defaultValue;
  return payload;
};

/**
 * Check if feature flags are initialized
 */
export const areFeatureFlagsInitialized = (): boolean => {
  return useFeatureFlagsStore.getState().isInitialized;
};

/**
 * Check if feature flags are loading
 */
export const areFeatureFlagsLoading = (): boolean => {
  return useFeatureFlagsStore.getState().isLoading;
};

/**
 * Set a local override for a feature flag
 */
export const setFlagOverride = (flagName: string, value: boolean | string | number): void => {
  const store = useFeatureFlagsStore.getState();

  let finalValue = value;
  if (typeof value === "boolean") {
    const originalValue = store.flags[flagName];
    if (typeof originalValue === "string" && isBooleanVariant(originalValue)) {
      const n = originalValue.toLowerCase();
      if (n === "enabled" || n === "disabled") finalValue = value ? "enabled" : "disabled";
      else if (n === "on" || n === "off") finalValue = value ? "on" : "off";
      else if (n === "true" || n === "false") finalValue = value ? "true" : "false";
    }
  }

  const newOverrides = { ...store.localOverrides, [flagName]: finalValue };
  store.setLocalOverrides(newOverrides);
  appStorage.set(AppStorageKeys.FEATURE_FLAGS_OVERRIDES, newOverrides);
};

/**
 * Clear a specific local override
 */
export const clearFlagOverride = (flagName: string): void => {
  const store = useFeatureFlagsStore.getState();
  const newOverrides = { ...store.localOverrides };
  delete newOverrides[flagName];

  store.setLocalOverrides(newOverrides);
  appStorage.set(AppStorageKeys.FEATURE_FLAGS_OVERRIDES, newOverrides);
};

/**
 * Clear all local overrides
 */
export const clearAllFlagOverrides = (): void => {
  useFeatureFlagsStore.getState().setLocalOverrides({});
  appStorage.remove(AppStorageKeys.FEATURE_FLAGS_OVERRIDES);
};

/**
 * Check if a flag has a local override
 */
export const hasFlagOverride = (flagName: string): boolean => {
  return useFeatureFlagsStore.getState().localOverrides[flagName] !== undefined;
};

/**
 * Get all local overrides
 */
export const getAllFlagOverrides = (): FeatureFlags => {
  return useFeatureFlagsStore.getState().localOverrides;
};

export const isFlagBooleanVariant = isBooleanVariant;
export const convertVariantToBoolean = variantToBoolean;

/**
 * Check if feature flags have been successfully fetched
 */
export const haveFeatureFlagsFetchedSuccessfully = (): boolean => {
  return useFeatureFlagsStore.getState().hasFetchedSuccessfully;
};

/**
 * Get the feature flag fetch error
 */
export const getFeatureFlagsFetchError = (): string | null => {
  return useFeatureFlagsStore.getState().fetchError;
};

/**
 * React hook for feature flags
 */
export const useFeatureFlags = () => {
  const flags = useFeatureFlagsStore((state) => state.flags);
  const localOverrides = useFeatureFlagsStore((state) => state.localOverrides);
  const isInitialized = useFeatureFlagsStore((state) => state.isInitialized);
  const isLoading = useFeatureFlagsStore((state) => state.isLoading);
  const hasFetchedSuccessfully = useFeatureFlagsStore((state) => state.hasFetchedSuccessfully);
  const fetchError = useFeatureFlagsStore((state) => state.fetchError);

  return {
    flags,
    localOverrides,
    isInitialized,
    isLoading,
    hasFetchedSuccessfully,
    fetchError,
    getBooleanFlag,
    getStringFlag,
    getNumberFlag,
    getFlagPayload,
    setFlagOverride,
    clearFlagOverride,
    clearAllFlagOverrides,
    hasFlagOverride,
    isFlagBooleanVariant,
    convertVariantToBoolean,
  };
};

/**
 * React hook that subscribes to a single boolean feature flag.
 * Returns a primitive boolean — guaranteed to trigger a re-render when the flag changes.
 */
export const useBooleanFlag = (flagName: string): boolean => {
  return useFeatureFlagsStore((state) => {
    const override = state.localOverrides[flagName];
    if (override !== undefined) return variantToBoolean(override);
    const value = state.flags[flagName];
    if (value !== undefined) return variantToBoolean(value);
    return false;
  });
};

/**
 * Feature flag names.
 * Values come from the server (cached or fresh) — no hardcoded defaults.
 * Zero-values (false / "" / 0) are returned when a flag is not in the store.
 */
export const FEATURE_FLAGS = {
  SITE_WIDE_CONTROLS: "site_wide_controls",
  FORCE_UPGRADE_CONFIG: "force_upgrade_config",
  LEGACY_AUTH: "legacy_auth",
  CACHING_CONFIG: "caching_config",
  ALARMS_TAB: "alarms_tab",
  AI_SCHEDULER: "ai_scheduler",
  DEV_PLAYGROUND: "dev_playground",
  ACTIVITY_TAB: "activity_tab",
  CAMERA_DETAILS_OPTION: "camera_details_option",
  USERS_PERMISSIONS_OPTION: "users_permissions_option",
  CAMERA_ALARM_OVERRIDE: "camera_alarm_override",
  CAMERA_ALARM_SILENCE: "camera_alarm_silence",
  CAMERA_ALARM_ADJUST: "camera_alarm_adjust",
  CAMERA_DETAILS_LIVE_CHIP: "camera_details_live_chip",
  ALARM_EXCEPTION_SUGGESTIONS: "alarm_exception_suggestions",
  EMAIL_CONTACT_SUGGESTIONS: "email_contact_suggestions",
  SHOW_LEGACY_APP_ON_STARTUP: "show_legacy_app_on_startup",
  AUTH0_MIGRATION_BANNER: "auth0_migration_banner",
  PUSH_NOTIFICATIONS: "push_notifications",
  TIMELAPSE_USERS: "timelapse_users",
  CAMERA_SEARCH: "camera_search",
} as const satisfies Record<string, string>;
